import type { EquipmentStandard } from '../types/work-scenarios';
import {
  calculateWindowsImprovement,
  calculateWallInsulationImprovement,
  calculateRoofInsulationImprovement,
  calculateFloorInsulationImprovement,
  calculateHeatingSystemImprovement,
  calculateHotWaterSystemImprovement,
  calculateVentilationImprovement,
  calculateHeatingControlImprovement,
  calculateProjectedPrimaryEnergy,
  calculateProjectedDPELabelFromEP,
  calculateTotalGESWithImprovements,
  calculateScenarioTotals,
} from './work-scenarios-calculator';

export type OptimizationStrategy = 'best_label' | 'best_value';

export interface ImprovementOption {
  category: string;
  equipment: EquipmentStandard;
  calculation: any;
  roi: number;
  efficiencyScore: number;
  labelImpact: number;
}

export interface OptimizedScenario {
  strategy: OptimizationStrategy;
  improvements: ImprovementOption[];
  totalInvestment: number;
  totalAnnualSavings: number;
  paybackPeriod: number;
  energySavings: number;
  gesReduction: number;
  currentLabel: string;
  projectedLabel: string;
  currentGESLabel: string;
  projectedGESLabel: string;
  currentEPperM2?: number;
  projectedEPperM2?: number;
  currentGESperM2?: number;
  projectedGESperM2?: number;
  summary: {
    workCount: number;
    labelGain: number;
    gesGainPercentage: number;
  };
}

interface DPEData {
  living_area: number;
  climate_zone: string;
  primary_energy_consumption?: number;
  greenhouse_gas_emissions?: number;
  deperditions_baies_vitrees?: number;
  deperditions_murs?: number;
  deperditions_planchers_hauts?: number;
  deperditions_planchers_bas?: number;
  deperditions_toiture?: number;
  deperditions_ponts_thermiques?: number;
  deperditions_enveloppe?: number;
  deperditions_renouvellement_air?: number;
  besoin_chauffage?: number;
  besoin_ecs?: number;
  conso_chauffage_ef?: number;
  conso_ecs_ef?: number;
  conso_eclairage_ef?: number;
  conso_auxiliaires_ef?: number;
  heating_energy?: string;
  hot_water_energy?: string;
  qualite_isolation_murs?: string;
  qualite_isolation_menuiseries?: string;
  qualite_isolation_plancher_haut_comble_perdu?: string;
  qualite_isolation_plancher_bas?: string;
  emission_ges_chauffage?: number;
  emission_ges_ecs?: number;
  emission_ges_eclairage?: number;
  emission_ges_auxiliaires?: number;
  emission_ges_5_usages?: number;
  [key: string]: any;
}

function getLabelValue(label: string): number {
  const labelMap: Record<string, number> = {
    'A': 7,
    'B': 6,
    'C': 5,
    'D': 4,
    'E': 3,
    'F': 2,
    'G': 1,
  };
  return labelMap[label] || 0;
}

function getCurrentLabel(dpe: DPEData): string {
  const consumptionPerM2 = dpe.primary_energy_consumption || 0;

  if (consumptionPerM2 <= 70) return 'A';
  if (consumptionPerM2 <= 110) return 'B';
  if (consumptionPerM2 <= 180) return 'C';
  if (consumptionPerM2 <= 250) return 'D';
  if (consumptionPerM2 <= 330) return 'E';
  if (consumptionPerM2 <= 420) return 'F';
  return 'G';
}

function getCurrentGESLabel(dpe: DPEData): string {
  const emissionsPerM2 = dpe.greenhouse_gas_emissions || 0;

  if (emissionsPerM2 <= 6) return 'A';
  if (emissionsPerM2 <= 11) return 'B';
  if (emissionsPerM2 <= 30) return 'C';
  if (emissionsPerM2 <= 50) return 'D';
  if (emissionsPerM2 <= 70) return 'E';
  if (emissionsPerM2 <= 100) return 'F';
  return 'G';
}

export type ProgressCallback = (progress: number, message: string) => void;

// Helper to yield control to the browser with proper delay
async function yieldToMainThread(): Promise<void> {
  return new Promise(resolve => {
    // Use requestIdleCallback if available, otherwise setTimeout with 10ms delay
    if (typeof requestIdleCallback !== 'undefined') {
      requestIdleCallback(() => resolve(), { timeout: 50 });
    } else {
      setTimeout(resolve, 10);
    }
  });
}

export async function calculateAllPossibleImprovements(
  dpe: DPEData,
  availableEquipments: {
    windows: EquipmentStandard[];
    wall_insulation: EquipmentStandard[];
    roof_insulation: EquipmentStandard[];
    floor_insulation: EquipmentStandard[];
    heating: EquipmentStandard[];
    hot_water: EquipmentStandard[];
    ventilation: EquipmentStandard[];
    heating_control: EquipmentStandard[];
  },
  userAnswers?: Record<string, any>,
  onProgress?: ProgressCallback
): Promise<ImprovementOption[]> {
  const improvements: ImprovementOption[] = [];

  // Cache current label calculations
  const currentLabel = getCurrentLabel(dpe);
  const currentLabelValue = getLabelValue(currentLabel);

  // Limit equipment per category to prevent excessive processing
  const MAX_EQUIPMENTS_PER_CATEGORY = 20;

  // Calculate total work items for progress tracking
  const limitedEquipments = {
    windows: availableEquipments.windows.slice(0, MAX_EQUIPMENTS_PER_CATEGORY),
    wall_insulation: availableEquipments.wall_insulation.slice(0, MAX_EQUIPMENTS_PER_CATEGORY),
    roof_insulation: availableEquipments.roof_insulation.slice(0, MAX_EQUIPMENTS_PER_CATEGORY),
    floor_insulation: availableEquipments.floor_insulation.slice(0, MAX_EQUIPMENTS_PER_CATEGORY),
    heating: availableEquipments.heating.slice(0, MAX_EQUIPMENTS_PER_CATEGORY),
    hot_water: availableEquipments.hot_water.slice(0, MAX_EQUIPMENTS_PER_CATEGORY),
    ventilation: availableEquipments.ventilation.slice(0, MAX_EQUIPMENTS_PER_CATEGORY),
    heating_control: availableEquipments.heating_control.slice(0, MAX_EQUIPMENTS_PER_CATEGORY),
  };

  const totalEquipments = Object.values(limitedEquipments).reduce(
    (sum, equipments) => sum + equipments.length,
    0
  );
  let processedEquipments = 0;

  console.log('Starting optimization with', totalEquipments, 'total equipments to process');

  const categoryLabels: Record<string, string> = {
    windows: 'Analyse des menuiseries',
    wall_insulation: 'Analyse de l\'isolation des murs',
    roof_insulation: 'Analyse de l\'isolation de la toiture',
    floor_insulation: 'Analyse de l\'isolation du plancher',
    heating: 'Analyse du système de chauffage',
    hot_water: 'Analyse de l\'eau chaude sanitaire',
    ventilation: 'Analyse de la ventilation',
    heating_control: 'Analyse de la régulation du chauffage',
  };

  let lastProgressUpdate = 0;
  const PROGRESS_UPDATE_INTERVAL = 100; // ms

  const addImprovementOptions = async (
    category: string,
    equipments: EquipmentStandard[],
    calculator: (dpe: DPEData, equipment: EquipmentStandard, answers?: Record<string, any>) => any
  ) => {
    const categoryStartTime = performance.now();
    console.log(`Starting category: ${category} with ${equipments.length} equipments`);

    for (let i = 0; i < equipments.length; i++) {
      const equipment = equipments[i];

      try {
        const calculation = calculator(dpe, equipment, userAnswers);

        // Validate calculation result
        if (calculation &&
            typeof calculation.energy_savings === 'number' &&
            calculation.energy_savings > 0) {

          const roi = calculation.estimated_cost > 0
            ? calculation.cost_savings / calculation.estimated_cost
            : 0;

          const efficiencyScore = calculation.estimated_cost > 0
            ? calculation.energy_savings / calculation.estimated_cost
            : calculation.energy_savings;

          // Calculate label impact
          const tempImprovements = [calculation];
          const primaryEnergyCalc = calculateProjectedPrimaryEnergy(
            dpe,
            tempImprovements,
            dpe.living_area
          );
          const { projectedLabel } = calculateProjectedDPELabelFromEP(
            primaryEnergyCalc.projectedEPperM2,
            dpe.living_area
          );

          const projectedLabelValue = getLabelValue(projectedLabel);
          const labelImpact = projectedLabelValue - currentLabelValue;

          improvements.push({
            category,
            equipment,
            calculation,
            roi,
            efficiencyScore,
            labelImpact,
          });
        }
      } catch (error) {
        console.error(`Failed to calculate improvement for ${category} equipment ${i}:`, {
          error,
          equipmentId: equipment.id,
          equipmentName: equipment.name
        });
      }

      processedEquipments++;

      // Throttle progress updates to avoid overwhelming the UI
      const now = performance.now();
      if (onProgress && totalEquipments > 0 && (now - lastProgressUpdate) >= PROGRESS_UPDATE_INTERVAL) {
        try {
          const progress = Math.round((processedEquipments / totalEquipments) * 100);
          const message = `${categoryLabels[category] || category}... (${i + 1}/${equipments.length})`;
          onProgress(progress, message);
          lastProgressUpdate = now;
        } catch (progressError) {
          console.error('Progress callback failed:', progressError);
        }
      }

      // Yield to main thread after EVERY equipment to prevent blocking
      await yieldToMainThread();
    }

    const categoryDuration = performance.now() - categoryStartTime;
    console.log(`Completed category: ${category} in ${categoryDuration.toFixed(0)}ms, found ${improvements.filter(i => i.category === category).length} valid improvements`);
  };

  // Process categories sequentially with progress updates
  const overallStartTime = performance.now();

  await addImprovementOptions('windows', limitedEquipments.windows, calculateWindowsImprovement);
  // Wall insulation equipments are pre-filtered by filterWallInsulationEquipments()
  // which includes breathable material filtering based on wall type (nature_murs)
  await addImprovementOptions('wall_insulation', limitedEquipments.wall_insulation, calculateWallInsulationImprovement);
  await addImprovementOptions('roof_insulation', limitedEquipments.roof_insulation, calculateRoofInsulationImprovement);
  await addImprovementOptions('floor_insulation', limitedEquipments.floor_insulation, calculateFloorInsulationImprovement);
  await addImprovementOptions('heating', limitedEquipments.heating, calculateHeatingSystemImprovement);
  await addImprovementOptions('hot_water', limitedEquipments.hot_water, calculateHotWaterSystemImprovement);
  await addImprovementOptions('ventilation', limitedEquipments.ventilation, calculateVentilationImprovement);
  await addImprovementOptions('heating_control', limitedEquipments.heating_control, calculateHeatingControlImprovement);

  const overallDuration = performance.now() - overallStartTime;
  console.log(`Optimization complete in ${(overallDuration / 1000).toFixed(1)}s. Found ${improvements.length} total improvements.`);

  // Note: We don't call onProgress(100) here because the service layer handles the final 100% callback
  // after generateOptimizedScenario completes. This prevents a race condition where the UI resets
  // before the modal opens.

  return improvements;
}

export async function optimizeForBestLabel(
  dpe: DPEData,
  allImprovements: ImprovementOption[]
): Promise<ImprovementOption[]> {
  const selectedImprovements: ImprovementOption[] = [];
  let currentDPE = { ...dpe };
  let currentLabelValue = getLabelValue(getCurrentLabel(dpe));
  const targetLabelValue = 7;

  const improvementsSortedByImpact = [...allImprovements].sort((a, b) => {
    return b.efficiencyScore - a.efficiencyScore;
  });

  const categoriesAdded = new Set<string>();

  console.log(`[OptimizeForBestLabel] Processing ${improvementsSortedByImpact.length} improvements...`);

  let iterationCount = 0;
  const MAX_ITERATIONS = 50; // Safety limit

  for (const improvement of improvementsSortedByImpact) {
    if (categoriesAdded.has(improvement.category)) {
      continue;
    }

    selectedImprovements.push(improvement);
    categoriesAdded.add(improvement.category);

    const calculations = selectedImprovements.map(imp => imp.calculation);
    const primaryEnergyCalc = calculateProjectedPrimaryEnergy(
      dpe,
      calculations,
      dpe.living_area
    );
    const { projectedLabel } = calculateProjectedDPELabelFromEP(
      primaryEnergyCalc.projectedEPperM2,
      dpe.living_area
    );

    currentLabelValue = getLabelValue(projectedLabel);

    iterationCount++;

    // Yield to main thread after each improvement selection
    await yieldToMainThread();

    if (currentLabelValue >= targetLabelValue || currentLabelValue >= 6) {
      console.log(`[OptimizeForBestLabel] Target reached: label ${projectedLabel} after ${iterationCount} iterations`);
      break;
    }

    if (iterationCount >= MAX_ITERATIONS) {
      console.warn(`[OptimizeForBestLabel] Max iterations (${MAX_ITERATIONS}) reached`);
      break;
    }
  }

  console.log(`[OptimizeForBestLabel] Completed with ${selectedImprovements.length} improvements`);
  return selectedImprovements;
}

export async function optimizeForBestValue(
  dpe: DPEData,
  allImprovements: ImprovementOption[]
): Promise<ImprovementOption[]> {
  const selectedImprovements: ImprovementOption[] = [];
  const currentLabelValue = getLabelValue(getCurrentLabel(dpe));
  const currentGESLabel = getCurrentGESLabel(dpe);
  const currentGESLabelValue = getLabelValue(currentGESLabel);
  
  // Cible : atteindre D (valeur 4) en énergie ET en GES
  const targetLabelValue = 4; // D
  const maxPaybackPeriod = 25;

  const improvementsSortedByROI = [...allImprovements]
    .filter(imp => imp.calculation.estimated_cost > 0)
    .sort((a, b) => b.efficiencyScore - a.efficiencyScore);

  const categoriesAdded = new Set<string>();
  let iterationCount = 0;
  const MAX_ITERATIONS = 50;

  for (const improvement of improvementsSortedByROI) {
    if (categoriesAdded.has(improvement.category)) continue;

    iterationCount++;

    const testImprovements = [...selectedImprovements, improvement];
    const calculations = testImprovements.map(imp => imp.calculation);
    const totals = calculateScenarioTotals(calculations);

    if (totals.paybackPeriod > maxPaybackPeriod) {
      if (iterationCount % 5 === 0) await yieldToMainThread();
      continue;
    }

    // Calculer la classe énergie projetée
    const primaryEnergyCalc = calculateProjectedPrimaryEnergy(
      dpe, calculations, dpe.living_area
    );
    const { projectedLabel } = calculateProjectedDPELabelFromEP(
      primaryEnergyCalc.projectedEPperM2, dpe.living_area
    );
    const projectedLabelValue = getLabelValue(projectedLabel);

    // Calculer la classe GES projetée
    const gesCalc = calculateTotalGESWithImprovements(
      dpe, calculations, dpe.living_area
    );
    const projectedGESLabelValue = getLabelValue(gesCalc.projectedLabel);

    selectedImprovements.push(improvement);
    categoriesAdded.add(improvement.category);

    await yieldToMainThread();

    // S'arrêter quand on atteint D en énergie ET D en GES
    const energyTargetReached = projectedLabelValue >= targetLabelValue;
    const gesTargetReached = projectedGESLabelValue >= targetLabelValue;

    if (energyTargetReached && gesTargetReached) {
      console.log(`[OptimizeForBestValue] Cible D atteinte : énergie=${projectedLabel}, GES=${gesCalc.projectedLabel}`);
      break;
    }

    if (iterationCount >= MAX_ITERATIONS) break;
  }

  return selectedImprovements;
}

export async function generateOptimizedScenario(
  dpe: DPEData,
  allImprovements: ImprovementOption[],
  strategy: OptimizationStrategy
): Promise<OptimizedScenario> {
  console.log(`[GenerateOptimizedScenario] Starting optimization with strategy: ${strategy}`);

  const selectedImprovements = strategy === 'best_label'
    ? await optimizeForBestLabel(dpe, allImprovements)
    : await optimizeForBestValue(dpe, allImprovements);

  // Yield after optimization selection
  await yieldToMainThread();

  console.log(`[GenerateOptimizedScenario] Computing final scenario metrics...`);

  const calculations = selectedImprovements.map(imp => imp.calculation);
  const totals = calculateScenarioTotals(calculations);

  const primaryEnergyCalc = calculateProjectedPrimaryEnergy(
    dpe,
    calculations,
    dpe.living_area
  );
  const { projectedLabel } = calculateProjectedDPELabelFromEP(
    primaryEnergyCalc.projectedEPperM2,
    dpe.living_area
  );

  // Yield after energy calculations
  await yieldToMainThread();

  const gesCalc = calculateTotalGESWithImprovements(
    dpe,
    calculations,
    dpe.living_area
  );

  const currentLabel = getCurrentLabel(dpe);
  const currentGESLabel = getCurrentGESLabel(dpe);
  const labelGain = getLabelValue(projectedLabel) - getLabelValue(currentLabel);

  const gesGainPercentage = gesCalc.currentTotal > 0
    ? ((gesCalc.currentTotal - gesCalc.projectedTotal) / gesCalc.currentTotal) * 100
    : 0;

  console.log(`[GenerateOptimizedScenario] Scenario complete: ${currentLabel} → ${projectedLabel} (${selectedImprovements.length} improvements)`);

  return {
    strategy,
    improvements: selectedImprovements,
    totalInvestment: totals.totalInvestment,
    totalAnnualSavings: totals.totalCostSavings,
    paybackPeriod: totals.paybackPeriod,
    energySavings: totals.totalEnergySavings,
    gesReduction: totals.totalGESReduction,
    currentLabel,
    projectedLabel,
    currentGESLabel,
    projectedGESLabel: gesCalc.projectedLabel,
    currentEPperM2: primaryEnergyCalc.currentEPperM2,
    projectedEPperM2: primaryEnergyCalc.projectedEPperM2,
    currentGESperM2: gesCalc.currentTotal,
    projectedGESperM2: gesCalc.projectedTotal,
    summary: {
      workCount: selectedImprovements.length,
      labelGain,
      gesGainPercentage: Math.round(gesGainPercentage),
    },
  };
}

export function rankImprovementsByEfficiency(
  improvements: ImprovementOption[]
): ImprovementOption[] {
  return [...improvements].sort((a, b) => b.efficiencyScore - a.efficiencyScore);
}

export function calculateROI(
  investment: number,
  annualSavings: number
): number {
  if (investment === 0 || annualSavings === 0) return 999;
  return investment / annualSavings;
}
